import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, Box, Fade, Typography, Icon } from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import MDTypography from "theme/components/MDTypography";
import MDBox from "theme/components/MDBox";

const steps = ["Creating form...", "Building form fields...", "Finalizing form..."];

export default function TemplateBuildingDialog({ onComplete, open: controlledOpen, onClose }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const [activeStep, setActiveStep] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setProgress(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (activeStep < steps.length) {
      const timer = setTimeout(() => {
        setActiveStep((prev) => {
          const next = prev + 1;
          setProgress((next / steps.length) * 100);
          return next;
        });
      }, 1500);

      return () => clearTimeout(timer);
    } else {
      const timer = setTimeout(() => {
        handleFinalAction();
        if (onClose) {
          onClose();
        } else {
          setInternalOpen(false);
        }
      }, 800);

      return () => clearTimeout(timer);
    }
  }, [activeStep, open]);

  const handleFinalAction = () => {
    if (onComplete) {
      onComplete();
    }
  };

  return (
    <>
      <Dialog open={open} maxWidth="xs" fullWidth onClose={onClose}>
        <DialogTitle sx={{ textAlign: "center", fontWeight: 600, fontSize: 16 }}>Setting up Template</DialogTitle>

        <DialogContent>
          {/* 🔄 Rotating Icon */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 0 }}>
            <Typography fontSize={48}>
              <Icon
                sx={{
                  fontSize: 40,
                  animation: activeStep >= steps.length ? "none" : "spin 1.5s linear infinite",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
                color={activeStep >= steps.length ? "success" : "none"}
              >
                {activeStep >= steps.length ? "check_circle" : "settings"}
              </Icon>
            </Typography>
          </Box>

          {/* Steps */}
          <Box sx={{ mt: 1, px: 3 }} align="start">
            {steps.map((step, index) => (
              <Fade in={activeStep >= index} key={step}>
                <MDTypography
                  variant="h6"
                  sx={{
                    opacity: activeStep > index ? 0.6 : 1,
                    fontWeight: activeStep === index ? 600 : 400,
                    mb: 1,
                  }}
                >
                  {activeStep > index ? "✔ " : "• "}
                  {step}
                </MDTypography>
              </Fade>
            ))}
          </Box>

          {/* Progress bar */}
          <MDBox mb={3}>
            <Box sx={{ px: 2, pt: 2 }}>
              <Box
                sx={{
                  height: 6,
                  borderRadius: 4,
                  backgroundColor: "#e0e0e0",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${progress}%`,
                    height: "100%",
                    backgroundColor: activeStep >= steps.length ? "success.main" : "info.main",
                    transition: "width 0.4s ease",
                  }}
                />
              </Box>
            </Box>
          </MDBox>
        </DialogContent>
      </Dialog>
    </>
  );
}
